import {
    FC, RefObject, useState, useCallback, useMemo,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, generatePath } from 'react-router-dom';
import Collapse from '@material-ui/core/Collapse';
import Snackbar from '@material-ui/core/Snackbar';
import CheckIcon from '@ui/Icons/Check';
import Routes, { RouteKey } from '@core/constants/Route';
import Grow from '@ui/Grow';
import StyledButton from '@ui/StyledButton';
import RenderDelayer from '@ui/RenderDelayer';
import { IStyledButtonModes } from '@ui/StyledButton/types';
import ButtonsWrapper from '@ui/ButtonsWrapper';
import firebaseService from '../../../services/Firebase';
import { IForm } from '../../../interfaces/Form';
import { ActionId } from '../../../constants/Analytics';
import ChatQuestion from '../../components-forms/ChatQuestion';
import { useUserState } from '../../context/UserContext';
import SnackbarContent from '../SnackbarContent';
import { ISnackbarContentMode } from '../SnackbarContent/types';

interface IFormSubmitterProperties {
    artisanProjectSource?: string
    isExternal?: boolean
    artisanId?: string
    artisanSlug?: string
    formState: IForm;
    onSelect?: (anchorRef?: RefObject<HTMLDivElement>) => void
}

const FormSubmitter: FC<IFormSubmitterProperties> = ({
    artisanProjectSource,
    isExternal,
    artisanId,
    artisanSlug,
    formState,
    onSelect,
}) => {
    const intl = useIntl();
    const history = useHistory();
    const { user } = useUserState();
    const [isLoading, setIsLoading] = useState(false);
    const [firebaseError, setFirebaseError] = useState();

    const { nosubmit } = useMemo(() => Object.fromEntries(new URLSearchParams(window.location.search).entries()), []);

    const resetFirebaseError = useCallback(() => setFirebaseError(undefined), [setFirebaseError]);

    const handleSubmissionCreation = useCallback(async () => {
        if (user) {
            try {
                setIsLoading(true);
                const submissionReference = await (await firebaseService.getInstance()).createSubmission(formState, undefined, artisanId, isExternal, artisanProjectSource);
                (await firebaseService.getInstance()).trackAction(ActionId.SUBMIT);
                setIsLoading(false);
                if (isExternal && artisanSlug) {
                    history.push(generatePath(Routes[RouteKey.SuccessArtisanDedicatedApp], {
                        formId       : formState.formId,
                        submissionId : submissionReference.id,
                        artisanSlug,
                    }));
                } else if (artisanSlug) {
                    history.push(generatePath(Routes[RouteKey.SuccessArtisanForms], {
                        formId       : formState.formId,
                        submissionId : submissionReference.id,
                        artisanSlug,
                    }));
                } else {
                    window.location.assign(generatePath(Routes[RouteKey.Success], {
                        formId       : formState.formId,
                        submissionId : submissionReference.id,
                    }));
                }
            } catch (error) {
                setFirebaseError(error.code);
                setIsLoading(false);
            }
        }
    }, [user, formState, artisanId, isExternal, artisanProjectSource, artisanSlug, history]);

    return (
        <>
            <ChatQuestion
                isExternal={isExternal}
                shouldAnimateOnMount
                onMountCallback={onSelect}
                onMountCallbackDelay={500}
            >
                <FormattedMessage id={isExternal ? 'components.FormComposer.completeAnswerExternal' : 'components.FormComposer.completeAnswer'} />
            </ChatQuestion>

            {!!user && (
                <RenderDelayer delay={1000}>
                    <Collapse timeout={400} in>
                        <Grow timeout={500} in>
                            <ButtonsWrapper>
                                <StyledButton
                                    mode={IStyledButtonModes.Large}
                                    loading={isLoading}
                                    error={!!firebaseError}
                                    icon={(<CheckIcon />)}
                                    onClick={handleSubmissionCreation}
                                    disabled={!!isLoading || !!firebaseError || !!nosubmit}
                                >
                                    <FormattedMessage id="components.FormComposer.completeSubmit" />
                                </StyledButton>
                            </ButtonsWrapper>
                        </Grow>
                    </Collapse>
                </RenderDelayer>
            )}

            {!!firebaseError && (
                <Snackbar
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    open={!!firebaseError}
                    onClose={resetFirebaseError}
                >
                    <SnackbarContent
                        mode={ISnackbarContentMode.Error}
                        message={intl.formatMessage({ id: `errors.firebase.${firebaseError}` })}
                        onClose={resetFirebaseError}
                    />
                </Snackbar>
            )}
        </>
    );
};

export default FormSubmitter;
